// App.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";

// App.js
import OHCAForm from "./pages/eCRF/forms/as7dk9f3n5v2x/forms/OHCAForm";
import OHCAEditForm from "./pages/eCRF/forms/as7dk9f3n5v2x/forms/OHCAEditForm";
import SubjectList from "./pages/eCRF/forms/as7dk9f3n5v2x/dashboard/SubjectList";
import APITest from "./pages/eCRF/forms/as7dk9f3n5v2x/components/APITest";
import LoginPage from "./pages/Login/LoginPage";
import RegisterPage from "./pages/Register/RegisterPage";
import Dashboard from "./pages/Dashboard/Dashboard";
import CreateProject from "./pages/CreateProject/CreateProject";
import Header from "./components/Header/Header";
import ECRFPage from "./pages/eCRF/ECRFPage";
import JoinProject from "./pages/JoinProject/JoinProject";
import HelpPage from "./pages/Help/HelpPage";
import NeuroICU11NewCase from "./pages/eCRF/newcase/vip/11/11NewCase";
import NeuroICU11Edit from "./pages/eCRF/newcase/vip/11/11Edit";
import NeuroICU11SubjectList from "./pages/eCRF/newcase/vip/11/11SubjectList";
import DemoNewCase from "./pages/eCRF/newcase/vip/demo2/2DemoNewCase";
import Demo3Page from "./pages/eCRF/newcase/vip/demo3/3DemoNewCase";
import Demo4Page from "./pages/eCRF/newcase/vip/demo4/4DemoNewCase";
import mainLogo from "./assets/main.png"; // 메인 로고 import
import "./App.css";

import TempTest from "./pages/Test/TempTest";

// 인증이 필요한 라우트를 위한 컴포넌트
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>로딩 중...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// 로딩 화면 컴포넌트
const LoadingScreen = () => (
  <div className="app-loading-screen">
    <img src={mainLogo} alt="Logo" className="loading-logo" />
    <p>Loading...</p>
  </div>
);

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 필요한 리소스 로드 또는 초기화 작업
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <AuthProvider>
      <LanguageProvider>
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
          <Router>
            <div className="App">
              <Routes>
                {/* 공개 라우트 */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />

                {/* 데모용 라우트 (인증 불필요) */}
                <Route path="/demo/2" element={<DemoNewCase />} />
                <Route path="/demo/3" element={<Demo3Page />} />
                <Route path="/demo/4" element={<Demo4Page />} />

                {/* 인증이 필요한 라우트 */}
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <Dashboard />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Test Page 라우트 */}
                <Route
                  path="/test-page"
                  element={
                    <PrivateRoute>
                      <>
                        <TempTest />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* eCRF 메인 페이지와 하위 경로들 */}
                <Route
                  path="/ecrf/*"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <ECRFPage />
                      </>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/create-project"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <CreateProject />
                      </>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/join-project"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <JoinProject />
                      </>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/api-test"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <APITest />
                      </>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/eCRF/forms/:groupId/subjectlist"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <SubjectList />
                      </>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/eCRF/forms/:groupId/newcase/*"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <OHCAForm />
                      </>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/:groupId/edit/:id"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <OHCAEditForm />
                      </>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/eCRF/forms/:groupId/api-test"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <APITest />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Help page */}
                <Route
                  path="/help"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <HelpPage />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Neuro-ICU 라우트 */}
                <Route
                  path="/ecrf/neuro-icu/11"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <NeuroICU11NewCase projectId="neuro-icu11" />
                      </>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/ecrf/neuro-icu/11/subjects"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <NeuroICU11SubjectList projectId="neuro-icu11" />
                      </>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/ecrf/neuro-icu/11/edit/:dataId"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <NeuroICU11Edit projectId="neuro-icu11" />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Edit 라우트 추가 */}
                <Route
                  path="/ecrf/update/:dataId"
                  element={
                    <PrivateRoute>
                      <>
                        <Header />
                        <ECRFPage />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* 메인 페이지 */}
                <Route path="/" element={<Navigate to="/login" replace />} />

                {/* 잘못된 GroupID로 접근시 메인으로 리다이렉트 */}
                <Route
                  path="/eCRF/forms/:groupId/*"
                  element={<InvalidGroupPage />}
                />
              </Routes>
            </div>
          </Router>
        </GoogleOAuthProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

// 잘못된 GroupID 페이지
const InvalidGroupPage = () => {
  return (
    <div className="invalid-group">
      <h2>잘못된 접근입니다</h2>
      <p>유효하지 않은 Group ID입니다.</p>
      <Navigate to="/" replace />
    </div>
  );
};

export default App;
